import { useCallback, useEffect, useState } from "react";
import { sendEmailVerification } from "firebase/auth";

import { Banner, Button, Spinner } from "@ui/react";
import { useAuth } from "../../lib/auth";
import { auth } from "../../lib/firebase";
import { firebaseAuthMessage } from "./firebaseErrors";
import { takeVerificationSendFailure } from "./verificationHandoff";
import { OnboardingLayout } from "./OnboardingLayout";

/** Long enough that Firebase's own rate limiter is never the thing that answers. */
const RESEND_COOLDOWN_SECONDS = 60;

export function VerifyEmailScreen() {
  const { user, reloadUser, signOut } = useAuth();

  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // Read once on mount and cleared, so a send failure that happened during
  // registration — where nothing could be rendered, because this screen was
  // already replacing that one — is reported here instead of vanishing.
  const [sendFailed, setSendFailed] = useState(() => takeVerificationSendFailure());

  /* Firebase never pushes an emailVerified change to an already-signed-in
   * session — the flag only updates when the client re-reads the user. Polling
   * is therefore the only way the tab that is waiting can notice the link being
   * clicked in another tab, or on a phone. */
  const check = useCallback(async () => {
    try {
      await reloadUser();
      setError(null);
    } catch (thrown) {
      setError(firebaseAuthMessage(thrown, "We couldn't check your verification status."));
    }
  }, [reloadUser]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      void check();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [check]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((seconds) => seconds - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const checkNow = async () => {
    setChecking(true);
    await check();
    setChecking(false);
  };

  const resend = async () => {
    const current = auth().currentUser;
    if (!current) return;

    setResending(true);
    setError(null);
    try {
      await sendEmailVerification(current);
      setResent(true);
      setSendFailed(false);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (thrown) {
      setError(firebaseAuthMessage(thrown, "We couldn't send another verification email."));
    } finally {
      setResending(false);
    }
  };

  return (
    <OnboardingLayout
      eyebrow="Verify your email"
      title="Check your inbox."
      lead={
        user?.email ? (
          <>
            We sent a verification link to <strong>{user.email}</strong>. Open it and this page will
            move you on by itself.
          </>
        ) : (
          "We sent you a verification link. Open it and this page will move you on by itself."
        )
      }
      step={2}
      footer={
        <Button variant="tertiary" onClick={() => void signOut()}>
          Sign out and use a different email
        </Button>
      }
    >
      <div className="stack">
        {/* Mail from this Firebase project is reliably filed as spam by Gmail;
            contractors who never look there simply stop onboarding here. */}
        <Banner tone="warn" title="Check your spam and promotions folders">
          Our verification mail often lands there. Mark it as "not spam" so job alerts reach you
          later on.
        </Banner>

        {sendFailed ? (
          <Banner tone="danger" title="We couldn't send your verification email">
            Nothing is wrong with your account — the email just didn't go out. Use Resend
            below and it should arrive shortly.
          </Banner>
        ) : null}

        {error ? <Banner tone="danger" title="Something went wrong">{error}</Banner> : null}

        {resent && !error ? (
          <Banner tone="info" title="Verification email sent">
            A fresh link is on its way to {user?.email ?? "your inbox"}.
          </Banner>
        ) : null}

        <div className="cluster" role="status">
          <Spinner />
          <span className="muted">Waiting for you to open the link…</span>
        </div>

        <div className="cluster">
          <Button onClick={() => void checkNow()} loading={checking}>
            I've verified my email
          </Button>

          <Button
            variant="secondary"
            onClick={() => void resend()}
            loading={resending}
            disabled={cooldown > 0}
          >
            Resend email
          </Button>
        </div>

        <span className="muted text-sm" role="status">
          {cooldown > 0 ? `You can resend in ${cooldown}s` : ""}
        </span>
      </div>
    </OnboardingLayout>
  );
}
